import React, { useState, useEffect, useCallback } from "react";
import "./AdminReports.css";
import {
  FiBarChart2,
  FiDownload,
  FiDollarSign,
  FiPieChart,
  FiShield,
  FiFilter,
  FiRefreshCw,
  FiAlertTriangle,
  FiLayers
} from "react-icons/fi";
import { getApiBaseUrl } from "../utils/apiConfig";

const API = getApiBaseUrl();

export default function AdminReports() {
  const [summary, setSummary] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [dateRange, setDateRange] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [channelType, setChannelType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchReports = useCallback(async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const headers = { Authorization: `Bearer ${token}` };

      let queryParams = "";
      if (dateRange === "7d") {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        queryParams = `?startDate=${d.toISOString().split("T")[0]}&endDate=${new Date().toISOString().split("T")[0]}`;
      } else if (dateRange === "30d") {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        queryParams = `?startDate=${d.toISOString().split("T")[0]}&endDate=${new Date().toISOString().split("T")[0]}`;
      } else if (dateRange === "custom" && startDate && endDate) {
        queryParams = `?startDate=${startDate}&endDate=${endDate}`;
      }

      const [summaryRes, settlementsRes, anomaliesRes] = await Promise.all([
        fetch(`${API}/admin/reports/summary${queryParams}`, { headers }),
        fetch(`${API}/admin/reports/settlements?type=${channelType}&limit=150`, { headers }),
        fetch(`${API}/admin/reports/fraud-anomalies`, { headers }),
      ]);

      if (summaryRes.ok) {
        const d = await summaryRes.json();
        setSummary(d.data);
      }
      if (settlementsRes.ok) {
        const d = await settlementsRes.json();
        setSettlements(d.data || []);
      }
      if (anomaliesRes.ok) {
        const d = await anomaliesRes.json();
        setAnomalies(d.data || []);
      }
    } catch (err) {
      console.error("Reports fetch error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, startDate, endDate, channelType]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Export CSV of Settlements
  const handleExportCSV = () => {
    if (!settlements || settlements.length === 0) return;

    const headers = [
      "Booking ID",
      "Category",
      "Service Name",
      "Partner Name",
      "Partner Email",
      "Customer",
      "Gateway",
      "Gross Amount (LKR)",
      "Platform Fee %",
      "Platform Cut (LKR)",
      "Partner Net Payout (LKR)",
      "Payment Status",
      "Date",
    ];

    const rows = filteredSettlements.map((s) => [
      s.booking_id,
      s.category,
      `"${(s.service_name || "").replace(/"/g, '""')}"`,
      `"${(s.partner_name || "").replace(/"/g, '""')}"`,
      s.partner_email || "",
      `"${(s.customer_name || "").replace(/"/g, '""')}"`,
      s.payment_gateway,
      Number(s.gross_amount).toFixed(2),
      s.platform_rate,
      Number(s.platform_cut).toFixed(2),
      Number(s.partner_net).toFixed(2),
      s.payment_status,
      new Date(s.created_at).toLocaleDateString(),
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `soloway_financial_settlements_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredSettlements = settlements.filter((s) => {
    const q = searchQuery.toLowerCase();
    return (
      (s.booking_id && String(s.booking_id).includes(q)) ||
      (s.service_name && s.service_name.toLowerCase().includes(q)) ||
      (s.partner_name && s.partner_name.toLowerCase().includes(q)) ||
      (s.customer_name && s.customer_name.toLowerCase().includes(q)) ||
      (s.payment_gateway && s.payment_gateway.toLowerCase().includes(q))
    );
  });

  if (loading) {
    return (
      <div className="admin-reports-page">
        <div className="reports-loading">
          <div className="spinner"></div>
          <p>Compiling executive financial statements & partner settlements...</p>
        </div>
      </div>
    );
  }

  const ch = summary?.channels || {};

  return (
    <div className="admin-reports-page">
      {/* Header */}
      <div className="reports-header">
        <div className="reports-title-group">
          <h2>
            <FiBarChart2 className="header-icon" /> Financial BI & Tax Settlements Desk
          </h2>
          <p>Itemized vendor disbursals, gateway breakdown (Payments.lk vs KoKo), and statutory audit reports.</p>
        </div>
        <div className="reports-actions">
          <button className="btn-reports-refresh" onClick={fetchReports} disabled={refreshing}>
            <FiRefreshCw className={refreshing ? "spinning" : ""} /> Refresh
          </button>
          <button className="btn-reports-export" onClick={handleExportCSV}>
            <FiDownload /> Export CSV Ledger
          </button>
        </div>
      </div>

      {/* Date Filter Toolbar */}
      <div className="reports-filter-bar">
        <div className="filter-group">
          <FiFilter className="filter-icon" />
          <span className="filter-label">Period:</span>
          <div className="filter-pills">
            <button className={`pill ${dateRange === "all" ? "active" : ""}`} onClick={() => setDateRange("all")}>
              All Time
            </button>
            <button className={`pill ${dateRange === "30d" ? "active" : ""}`} onClick={() => setDateRange("30d")}>
              Last 30 Days
            </button>
            <button className={`pill ${dateRange === "7d" ? "active" : ""}`} onClick={() => setDateRange("7d")}>
              Last 7 Days
            </button>
            <button className={`pill ${dateRange === "custom" ? "active" : ""}`} onClick={() => setDateRange("custom")}>
              Custom Range
            </button>
          </div>
        </div>

        {dateRange === "custom" && (
          <div className="custom-date-inputs">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span>to</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        )}
      </div>

      {/* Executive Financial KPI Cards */}
      <div className="reports-kpi-grid">
        <div className="reports-kpi-card">
          <div className="kpi-icon-box blue">
            <FiDollarSign />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Gross Transaction Volume</span>
            <span className="kpi-value">Rs. {Number(summary?.grossVolume || 0).toLocaleString()}</span>
            <span className="kpi-sub">{summary?.totalTransactions || 0} Total Bookings</span>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="kpi-icon-box emerald">
            <FiPieChart />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Net Platform Commission</span>
            <span className="kpi-value">Rs. {Number(summary?.netPlatformRevenue || 0).toLocaleString()}</span>
            <span className="kpi-sub text-emerald">After 5% Agent Referral Allocation</span>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="kpi-icon-box purple">
            <FiLayers />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Partner Payout Disbursals</span>
            <span className="kpi-value">Rs. {Number(summary?.vendorPayouts || 0).toLocaleString()}</span>
            <span className="kpi-sub">Guaranteed 90% (Payments.lk) / 80% (KoKo)</span>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="kpi-icon-box amber">
            <FiShield />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Estimated 1% Tax Withholding</span>
            <span className="kpi-value">Rs. {Number(summary?.taxWithholding || 0).toLocaleString()}</span>
            <span className="kpi-sub">Statutory Compliance Provision</span>
          </div>
        </div>
      </div>

      {/* Channel Distribution Cards */}
      <div className="channel-distribution-grid">
        <div className="channel-box">
          <div className="channel-title">🏨 Hotel Accommodations</div>
          <div className="channel-metrics">
            <span className="channel-gross">Rs. {Number(ch.hotels?.gross || 0).toLocaleString()}</span>
            <span className="channel-cut">Commission: Rs. {Number(ch.hotels?.commission || 0).toLocaleString()}</span>
          </div>
          <span className="channel-count">{ch.hotels?.count || 0} Stays Booked</span>
        </div>

        <div className="channel-box">
          <div className="channel-title">🧭 Tour Experiences</div>
          <div className="channel-metrics">
            <span className="channel-gross">Rs. {Number(ch.tours?.gross || 0).toLocaleString()}</span>
            <span className="channel-cut">Commission: Rs. {Number(ch.tours?.commission || 0).toLocaleString()}</span>
          </div>
          <span className="channel-count">{ch.tours?.count || 0} Tours Guided</span>
        </div>

        <div className="channel-box">
          <div className="channel-title">🎟️ Events & Festivals</div>
          <div className="channel-metrics">
            <span className="channel-gross">Rs. {Number(ch.events?.gross || 0).toLocaleString()}</span>
            <span className="channel-cut">Commission: Rs. {Number(ch.events?.commission || 0).toLocaleString()}</span>
          </div>
          <span className="channel-count">{ch.events?.count || 0} Tickets Issued</span>
        </div>

        <div className="channel-box">
          <div className="channel-title">🛂 ETA Visa Services</div>
          <div className="channel-metrics">
            <span className="channel-gross">Rs. {Number(ch.visas?.gross || 0).toLocaleString()}</span>
            <span className="channel-cut">Commission: Rs. {Number(ch.visas?.commission || 0).toLocaleString()}</span>
          </div>
          <span className="channel-count">{ch.visas?.count || 0} Applications Processed</span>
        </div>
      </div>

      {/* Fraud & Risk Anomalies Alert Panel (if any) */}
      {anomalies.length > 0 && (
        <div className="reports-anomalies-card">
          <div className="anomalies-header">
            <h3>
              <FiAlertTriangle className="alert-icon" /> Automated Fraud & Risk Anomaly Monitor
            </h3>
            <span className="anomalies-badge">{anomalies.length} Flagged</span>
          </div>
          <div className="anomalies-list">
            {anomalies.map((item, idx) => (
              <div key={idx} className="anomaly-item">
                <span className="anomaly-severity high">Risk Alert</span>
                <div className="anomaly-details">
                  <strong>{item.full_name || item.email}</strong>
                  <span className="anomaly-reason">{item.risk_reason} ({item.failed_attempts || item.withdrawal_count} occurrences)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Itemized Settlements Table Card */}
      <div className="reports-table-card">
        <div className="table-card-head">
          <div className="table-title-area">
            <h3>Itemized Partner Settlements & Disbursal Ledger</h3>
            <span className="table-sub">{filteredSettlements.length} Transactions</span>
          </div>

          <div className="table-toolbar">
            <select
              value={channelType}
              onChange={(e) => setChannelType(e.target.value)}
              className="table-channel-select"
            >
              <option value="all">All Channels</option>
              <option value="hotel">Hotels Stays Only</option>
              <option value="tour">Guided Tours Only</option>
            </select>

            <input
              type="text"
              placeholder="Search partner, service, booking ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="table-search-input"
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Booking #</th>
                <th>Category</th>
                <th>Service & Host</th>
                <th>Customer</th>
                <th>Payment Gateway</th>
                <th>Gross Total</th>
                <th>Platform Fee</th>
                <th>Partner Payout</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredSettlements.map((s, idx) => (
                <tr key={idx}>
                  <td className="font-mono">#{s.booking_id}</td>
                  <td>
                    <span className="badge-category">{s.category}</span>
                  </td>
                  <td>
                    <div className="service-info-cell">
                      <span className="service-name">{s.service_name}</span>
                      <span className="partner-name">Host: {s.partner_name}</span>
                    </div>
                  </td>
                  <td>{s.customer_name}</td>
                  <td>
                    <span className={`badge-gateway ${s.payment_gateway === "KOKO BNPL" ? "koko" : "payments_lk"}`}>
                      {s.payment_gateway}
                    </span>
                  </td>
                  <td className="font-bold">Rs. {Number(s.gross_amount).toLocaleString()}</td>
                  <td className="text-muted">
                    Rs. {Number(s.platform_cut).toFixed(2)} ({s.platform_rate})
                  </td>
                  <td className="font-bold text-success">Rs. {Number(s.partner_net).toFixed(2)}</td>
                  <td>
                    <span className="badge-status-done">{s.payment_status}</span>
                  </td>
                  <td className="text-muted text-sm">{new Date(s.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {filteredSettlements.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center py-6 text-muted">
                    No transactions found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
